import {
  Area,
  Farm,
  PointDefinition,
  Zone,
  ZoneContents,
} from "protos/farm/farm";
import {
  BoundaryEdit,
  BoundaryRingId,
  DraftBoundaryPoint,
  findRing,
} from "portal/state/farmExplorer";
import { DateTime } from "luxon";
import { DeepPartial } from "portal/utils/objects";
import { FixType, Point, PolygonRing } from "protos/geo/geo";
import { makeGeoId } from "common/utils/geo";
import { polygon } from "@turf/helpers";
import kinks from "@turf/kinks";

export type UpdateFarm = (
  updates: DeepPartial<Farm>
) => Promise<boolean> /* resolves false if the save failed */;

// converts a stored (closed) polygon ring to an un-closed draft point list
export const ringToDraftPoints = (
  ring: PolygonRing,
  pointsById: Record<string, Point>
): DraftBoundaryPoint[] => {
  let refs = ring.points;
  const first = refs[0];
  const last = refs.at(-1);
  // stored rings repeat the first point at the end; drop the duplicate
  if (refs.length > 1 && first?.id?.id && first.id.id === last?.id?.id) {
    refs = refs.slice(0, -1);
  }
  const draft: DraftBoundaryPoint[] = [];
  const seen = new Set<string>();
  for (const ref of refs) {
    const id = ref.id?.id;
    if (!id) {
      continue;
    }
    // the draft is keyed by point id, so a ring that visits a point twice
    // keeps only the first visit
    if (seen.has(id)) {
      console.warn(`ringToDraftPoints: dropping repeated point ${id}`);
      continue;
    }
    seen.add(id);
    const point = pointsById[id] ?? ref;
    draft.push({
      pointId: id,
      lat: point.lat,
      lng: point.lng,
      ordinal: draft.length + 1,
      fixType: point.captureInfo?.fixType,
    });
  }
  return draft;
};

// squared distance from point p to segment ab, in a locally-flat projection
const distanceToSegmentSquared = (
  p: [number, number],
  a: [number, number],
  b: [number, number]
): number => {
  const abx = b[0] - a[0];
  const aby = b[1] - a[1];
  const apx = p[0] - a[0];
  const apy = p[1] - a[1];
  const lengthSquared = abx * abx + aby * aby;
  let t = lengthSquared === 0 ? 0 : (apx * abx + apy * aby) / lengthSquared;
  t = Math.max(0, Math.min(1, t));
  const dx = p[0] - (a[0] + t * abx);
  const dy = p[1] - (a[1] + t * aby);
  return dx * dx + dy * dy;
};

/**
 * Index of the ring edge nearest to (lng, lat): the edge from `points[i]` to
 * `points[i + 1]` (wrapping), i.e. the one a new point there would split.
 * Returns undefined when there is no edge yet.
 */
// [lng, lat] positions of a ring with the first point repeated at the end
export const closedRingPositions = (
  points: { lat: number; lng: number }[]
): [number, number][] => {
  const positions = points.map((pt): [number, number] => [pt.lng, pt.lat]);
  const [first] = positions;
  return first ? [...positions, first] : positions;
};

export const nearestEdgeIndex = (
  points: { lat: number; lng: number }[],
  lng: number,
  lat: number
): number | undefined => {
  if (points.length < 2) {
    return;
  }
  // approximate local flat projection: scale longitudes by cos(latitude)
  const kx = Math.cos((lat * Math.PI) / 180);
  const project = (pt: { lng: number; lat: number }): [number, number] => [
    pt.lng * kx,
    pt.lat,
  ];
  const target = project({ lng, lat });
  let bestIndex = 0;
  let bestDistance = Infinity;
  for (const [i, a] of points.entries()) {
    const b = points[(i + 1) % points.length];
    if (!b) {
      continue;
    }
    const distance = distanceToSegmentSquared(target, project(a), project(b));
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = i;
    }
  }
  return bestIndex;
};

// inserts a new draft point into the ring edge nearest to (lng, lat)
export const insertPointOnNearestEdge = (
  points: DraftBoundaryPoint[],
  lng: number,
  lat: number,
  pointId: string = makeGeoId()
): DraftBoundaryPoint[] => {
  // labels are stable for the life of the edit, so a new point takes the next
  // unused number rather than renumbering the ring
  const ordinal = Math.max(0, ...points.map((pt) => pt.ordinal)) + 1;
  const bestIndex = nearestEdgeIndex(points, lng, lat);
  if (bestIndex === undefined) {
    return [
      ...points,
      { pointId, lat, lng, ordinal, fixType: FixType.NO_FIX, isNew: true },
    ];
  }
  const next = [...points];
  next.splice(bestIndex + 1, 0, {
    pointId,
    lat,
    lng,
    ordinal,
    fixType: FixType.NO_FIX,
    isNew: true,
  });
  return next;
};

// whether the draft ring differs from the one the edit started with; a point
// dragged away and back again counts as unchanged, which is what a user means
// by "no changes"
export const isBoundaryEditDirty = (edit: BoundaryEdit): boolean => {
  const { points, originalPoints, pending } = edit;
  if (pending?.name.trim()) {
    return true;
  }
  if (points.length !== originalPoints.length) {
    return true;
  }
  return points.some((pt, i) => {
    const original = originalPoints[i];
    return (
      pt.pointId !== original?.pointId ||
      pt.lat !== original.lat ||
      pt.lng !== original.lng
    );
  });
};

// a hand-placed position is not surveyed, so a moved point drops to no-fix
export const moveDraftPoint = (
  points: DraftBoundaryPoint[],
  pointId: string,
  lng: number,
  lat: number
): DraftBoundaryPoint[] =>
  points.map((pt) =>
    pt.pointId === pointId ? { ...pt, lat, lng, fixType: FixType.NO_FIX } : pt
  );

const draftPointProto = (pt: DraftBoundaryPoint): Point => ({
  id: { id: pt.pointId },
  lat: pt.lat,
  lng: pt.lng,
  alt: 0,
  name: "",
  captureInfo: {
    fixType: pt.fixType ?? FixType.NO_FIX,
    captureTime: "",
  },
});

/**
 * Returns the farm with the ring being edited taken out, so the stored
 * geometry doesn't sit underneath the draft. The draft itself is drawn by
 * BoundaryDraftLayer.
 *
 * Deliberately depends only on which ring is being edited, never on the draft
 * points: deriving FarmMapData runs turf buffer/mask over every obstacle, far
 * too expensive to redo on every frame of a drag.
 */
export const farmWithoutRing = (farm: Farm, ring: BoundaryRingId): Farm => {
  if (ring.holeIndex === undefined) {
    // a polygon cannot exist without its boundary, so drop the whole zone
    return {
      ...farm,
      zones: farm.zones.filter((z) => z.id?.id !== ring.zoneId),
    };
  }
  return {
    ...farm,
    zones: farm.zones.map((zone) => {
      if (zone.id?.id !== ring.zoneId) {
        return zone;
      }
      return {
        ...zone,
        areas: zone.areas.map((area, i) =>
          i === ring.areaIndex && area.polygon
            ? {
                ...area,
                polygon: {
                  ...area.polygon,
                  holes: area.polygon.holes.filter(
                    (_hole, hi) => hi !== ring.holeIndex
                  ),
                },
              }
            : area
        ),
      };
    }),
  };
};

// collects the IDs of every point referenced by the given zones
const collectReferencedPointIds = (zones: DeepPartial<Zone>[]): Set<string> => {
  const ids = new Set<string>();
  const add = (point?: DeepPartial<Point>): void => {
    if (point?.id?.id) {
      ids.add(point.id.id);
    }
  };
  for (const zone of zones) {
    if (zone.version?.deleted) {
      continue;
    }
    for (const area of zone.areas ?? []) {
      add(area.point);
      for (const pt of area.lineString?.points ?? []) {
        add(pt);
      }
      for (const pt of area.polygon?.boundary?.points ?? []) {
        add(pt);
      }
      for (const hole of area.polygon?.holes ?? []) {
        for (const pt of hole.points ?? []) {
          add(pt);
        }
      }
    }
    add(zone.contents?.field?.plantingHeading?.abLine?.a);
    add(zone.contents?.field?.plantingHeading?.abLine?.b);
    add(zone.contents?.centerPivot?.center);
  }
  return ids;
};

// builds the single updateFarm patch that persists a boundary edit: the zone
// with its new ring, moved/new point definitions, and soft-deletes for points
// that nothing references anymore
export const buildBoundaryEditPatch = (
  farm: Farm,
  edit: BoundaryEdit
): DeepPartial<Farm> | undefined => {
  const zone = farm.zones.find((z) => z.id?.id === edit.zoneId);
  const ring = findRing(farm, edit);
  if (!zone || !ring) {
    return;
  }
  const now = DateTime.now().toISO();
  // ring entries are id-only references; coordinates live in the pointDefs
  const ringRefs = [...edit.points, ...edit.points.slice(0, 1)].map((pt) => ({
    id: { id: pt.pointId },
  }));
  const zonePatch: DeepPartial<Zone> = {
    ...zone,
    areas: zone.areas.map((area, i) => {
      if (i !== edit.areaIndex || !area.polygon) {
        return area;
      }
      const polygon: DeepPartial<Zone["areas"][number]["polygon"]> =
        edit.holeIndex === undefined
          ? { ...area.polygon, boundary: { points: ringRefs } }
          : {
              ...area.polygon,
              holes: area.polygon.holes.map((hole, hi) =>
                hi === edit.holeIndex ? { points: ringRefs } : hole
              ),
            };
      return { ...area, polygon };
    }),
    version: { ...zone.version, changed: true, deleted: false },
  };

  const pointDefsById = new Map(
    farm.pointDefs.flatMap((pd): [string, PointDefinition][] =>
      pd.point?.id?.id ? [[pd.point.id.id, pd]] : []
    )
  );
  const pointDefs: DeepPartial<PointDefinition>[] = [];
  for (const draft of edit.points) {
    const existing = pointDefsById.get(draft.pointId);
    if (!existing?.point) {
      pointDefs.push({
        point: {
          ...draftPointProto(draft),
          name: `Point.${draft.pointId}`,
          captureInfo: { fixType: FixType.NO_FIX, captureTime: now },
        },
      });
    } else if (
      existing.point.lat !== draft.lat ||
      existing.point.lng !== draft.lng
    ) {
      pointDefs.push({
        point: {
          ...existing.point,
          lat: draft.lat,
          lng: draft.lng,
          // the stored position was surveyed; this one was placed by hand
          captureInfo: {
            ...existing.point.captureInfo,
            fixType: FixType.NO_FIX,
            captureTime: now,
          },
        },
        version: {
          ordinal: existing.version?.ordinal ?? 1,
          updateTime: now,
          changed: true,
          deleted: false,
        },
      });
    }
  }

  // soft-delete removed points that no remaining geometry references
  const draftIds = new Set(edit.points.map((pt) => pt.pointId));
  const removedIds = new Set<string>();
  for (const ref of ring.points) {
    const id = ref.id?.id;
    if (id && !draftIds.has(id)) {
      removedIds.add(id);
    }
  }
  if (removedIds.size > 0) {
    const patchedZones: DeepPartial<Zone>[] = farm.zones.map((z) =>
      z.id?.id === edit.zoneId ? zonePatch : z
    );
    const referenced = collectReferencedPointIds(patchedZones);
    for (const id of removedIds) {
      const existing = pointDefsById.get(id);
      if (existing && !referenced.has(id)) {
        pointDefs.push({
          point: existing.point,
          version: { ...existing.version, deleted: true },
        });
      }
    }
  }

  return {
    // NOTE: the patch logic of the patch farm API handles a missing "name"
    // field as a request to set the farm name to the empty string.
    id: farm.id,
    version: farm.version,
    name: farm.name,
    zones: [zonePatch],
    pointDefs,
  };
};

type PlacedPoint = Pick<DraftBoundaryPoint, "pointId" | "lat" | "lng">;

export type NewZoneGeometry =
  /** a polygon zone: the ring's corners in order, un-closed */
  | { ring: PlacedPoint[] }
  /**
   * a zone with no area of its own, like a center pivot, whose contents
   * reference this one point by id
   */
  | { center: PlacedPoint };

export interface NewZone {
  zoneId: string;
  name: string;
  contents: DeepPartial<ZoneContents>;
  geometry: NewZoneGeometry;
}

// a farm has at most one boundary, always stored under this name
const FARM_BOUNDARY_NAME = "farm boundary";

// as wide as the blank leg the center pivot details panel adds
const DEFAULT_LEG_WIDTH_METERS = 1;

/**
 * The zone a pending boundary edit creates. Undefined for an edit of an
 * existing ring, and for a center pivot whose center is not placed yet.
 */
export const newZoneFromEdit = (edit: BoundaryEdit): NewZone | undefined => {
  const { zoneId, points, pending } = edit;
  if (!pending) {
    return;
  }
  const name = pending.name.trim();
  switch (pending.kind) {
    case "farmBoundary": {
      return {
        zoneId,
        name: FARM_BOUNDARY_NAME,
        contents: { farmBoundary: {} },
        geometry: { ring: points },
      };
    }
    case "obstacle": {
      return {
        zoneId,
        name,
        contents: { obstacle: { passable: pending.passable } },
        geometry: { ring: points },
      };
    }
    case "centerPivot": {
      const [center] = points;
      if (!center) {
        return;
      }
      return {
        zoneId,
        name,
        contents: {
          centerPivot: {
            center: { id: { id: center.pointId } },
            legs: pending.armLengthMeters
              ? [
                  {
                    widthMeters: DEFAULT_LEG_WIDTH_METERS,
                    lengthMeters: pending.armLengthMeters,
                    endpointDeviceId: "",
                  },
                ]
              : [],
          },
        },
        geometry: { center },
      };
    }
  }
};

export type BoundaryEditProblem =
  | "notEnoughPoints"
  | "selfIntersecting"
  | "nameRequired";

/**
 * The first thing wrong with a boundary edit, if any. Any problem blocks
 * saving a new zone; an existing ring that crosses itself may still be saved,
 * since the backend accepts mild self-intersections in recorded rings.
 */
export const boundaryEditProblem = ({
  points,
  pending,
}: Pick<BoundaryEdit, "points" | "pending">):
  | BoundaryEditProblem
  | undefined => {
  if (pending?.kind === "centerPivot") {
    if (points.length === 0) {
      return "notEnoughPoints";
    }
  } else if (points.length < 3) {
    return "notEnoughPoints";
  } else if (
    kinks(polygon([closedRingPositions(points)])).features.length > 0
  ) {
    return "selfIntersecting";
  }
  if (pending && pending.kind !== "farmBoundary" && !pending.name.trim()) {
    return "nameRequired";
  }
};

/**
 * Builds the updateFarm patch that creates a zone from points placed on the
 * map: one hand-placed point definition per point, and the zone that
 * references them by id (as its polygon ring, or from its contents). Nothing
 * else about the farm is touched: the backend accepts a zone or point it has
 * not seen before without a version check, and leaving the farm's own version
 * out keeps a concurrent rename from turning this into a conflict.
 */
export const buildNewZonePatch = (
  farm: Farm,
  zone: NewZone,
  now: string = DateTime.now().toISO()
): DeepPartial<Farm> => {
  const points =
    "ring" in zone.geometry ? zone.geometry.ring : [zone.geometry.center];
  // ring entries are id-only references; coordinates live in the pointDefs
  const areas: DeepPartial<Area>[] =
    "ring" in zone.geometry
      ? [
          {
            bufferMeters: 0,
            polygon: {
              boundary: {
                points: [...points, ...points.slice(0, 1)].map((pt) => ({
                  id: { id: pt.pointId },
                })),
              },
              holes: [],
            },
          },
        ]
      : [];
  return {
    id: farm.id,
    pointDefs: points.map((pt) => ({
      point: {
        id: { id: pt.pointId },
        lng: pt.lng,
        lat: pt.lat,
        alt: 0,
        name: `Point.${pt.pointId}`,
        captureInfo: { fixType: FixType.NO_FIX, captureTime: now },
      },
    })),
    zones: [
      {
        id: { id: zone.zoneId },
        name: zone.name,
        areas,
        contents: zone.contents,
      },
    ],
  };
};
